import { code, folder, person } from 'ionicons/icons';

import { AZ_CLIENT_ID, OAUTH_REDIRECT_URI } from '../constants/secrets';
import { ServiceTypes } from '../constants/service';
import {
  AzureCommit,
  AzureCommits,
  AzureCurrentUser,
  AzureIdentity,
  AzureProjects,
  AzureRepo,
  AzureRepos,
  AzureUser
} from '../types/azure.types';
import { Commit, Organization, User } from '../types/services.types';
import { determineDefaultAvatarForUser } from '../util/defaultAvatarUrl';
import { ServiceError } from './errors';
import { get } from './requests';
import { Service } from './service';

export class Azure implements Service {
  readonly id = ServiceTypes.Azure;
  readonly host: string;
  readonly hostDomain: string;
  readonly name = 'Azure DevOps';
  readonly isCloud: boolean;

  readonly #authUrl: string;

  readonly emailsToIgnore = [];
  readonly tokenScopes = [
    'Code Read',
    'Graph Read',
    'Identity Read',
    'Project and Team Read',
    'User Profile Read',
    'Work Items Read'
  ];
  readonly oauthScopes = ['vso.code', 'vso.graph'];
  readonly patName = 'Access Token';
  readonly placeholderPat =
    'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx';
  readonly patRegex = `^.{${this.placeholderPat.length}}$`;
  readonly patUsername = false;
  readonly patUsernameLink = null;

  readonly orgText = 'organization';
  readonly repoText = 'project';

  readonly defaultAvatar = `/img/${this.id}.svg`;

  #usingPat: boolean;

  constructor(host: string, usingPat: boolean) {
    this.host = host;
    this.hostDomain = host.replace('https://', '').replace('http://', '');
    this.isCloud = host === 'https://dev.azure.com';
    this.#usingPat = usingPat;

    if (this.isCloud) {
      this.#authUrl = `https://vssps.${this.hostDomain}`;
    } else {
      this.#authUrl = this.host;
    }
  }

  getOAuthLink(state: string): string {
    return `https://app.vssps.visualstudio.com/oauth2/authorize?client_id=${AZ_CLIENT_ID}&response_type=Assertion&state=${state}&scope=${this.oauthScopes.join('%20')}&redirect_uri=${OAUTH_REDIRECT_URI}`;
  }

  getTokenUrl(org: string, ide: string, machine?: string): string {
    return `${this.host}/${org}/_usersSettings/tokens`;
  }

  async hasAccessToRepoProvided(
    token: string,
    org: string,
    repo: string | null
  ): Promise<boolean> {
    if (!repo) return true;
    try {
      const [projectName, repoName] = repo.split('/');
      await this.#get<AzureRepo>(
        token,
        `${this.host}/${org}/${projectName}/_apis/git/repositories/${repoName || projectName}?api-version=7.1-preview.1`
      );

      return true;
    } catch (error: any) {
      return false;
    }
  }

  async getUser(token: string, org: string): Promise<User> {
    const currentUserResult = await this.#get<AzureCurrentUser>(
      token,
      `${this.host}/${org}/_apis/ConnectionData`
    );

    if (!this.isCloud) {
      const identityResult = await this.#get<AzureIdentity>(
        token,
        `${this.#authUrl}/${org}/_apis/identities?api-version=7.1&subjectDescriptors=${currentUserResult.authenticatedUser.subjectDescriptor}`
      );

      const userResult = identityResult.value[0];
      const email = userResult.properties?.Mail?.$value;

      if (!email) {
        throw new ServiceError(
          'No email found for user',
          `No email found for current user ${userResult.properties?.Account?.$value}`
        );
      }

      return {
        id: currentUserResult.authenticatedUser.subjectDescriptor,
        photoURL: `${this.#authUrl}/${org}/_api/_common/IdentityImage?id=${userResult.id}`,
        displayName: userResult.displayName,
        login: userResult.directoryAlias,
        email: email,
        service: this.id
      };
    }

    const userResult = await this.#get<AzureUser>(
      token,
      `${this.#authUrl}/${org}/_apis/graph/users/${currentUserResult.authenticatedUser.subjectDescriptor}?api-version=7.1-preview.1`
    );

    return {
      id: currentUserResult.authenticatedUser.subjectDescriptor,
      photoURL: userResult._links.avatar.href,
      displayName: userResult.displayName,
      login: userResult.directoryAlias,
      email: userResult.mailAddress,
      service: this.id
    };
  }

  async getUserEmails(token: string, org: string): Promise<string[]> {
    const currentUserResult = await this.#get<AzureCurrentUser>(
      token,
      `${this.host}/${org}/_apis/ConnectionData`
    );

    if (!this.isCloud) {
      const identityResult = await this.#get<AzureIdentity>(
        token,
        `${this.#authUrl}/${org}/_apis/identities?api-version=7.1&subjectDescriptors=${currentUserResult.authenticatedUser.subjectDescriptor}`
      );

      const userResult = identityResult.value[0];
      const email = userResult.properties?.Mail?.$value;

      if (!email) {
        throw new ServiceError(
          'No email found for user',
          `No email found for current user ${userResult.properties?.Account?.$value}`
        );
      }

      return [email];
    }

    const userResult = await this.#get<AzureUser>(
      token,
      `${this.#authUrl}/${org}/_apis/graph/users/${currentUserResult.authenticatedUser.subjectDescriptor}?api-version=7.1-preview.1`
    );

    return [userResult.mailAddress];
  }

  async getOrganization(token: string, org: string): Promise<Organization> {
    // There is no name and avatar for the org so use a default icon
    const currentOrgResult = await this.#get<AzureCurrentUser>(
      token,
      `${this.host}/${org}/_apis/ConnectionData`
    );

    return {
      id: currentOrgResult.instanceId,
      photoURL: determineDefaultAvatarForUser(org, org),
      displayName: org,
      type: 'account',
      createdAt: '',
      enterprise: !this.isCloud
    };
  }

  async getMostRecentRepos(token: string, org: string): Promise<string[]> {
    // Fetch the top 5 projects and then fetch the top 5 repos from each project
    const topProjectsResults = await this.#get<AzureProjects>(
      token,
      `${this.host}/${org}/_apis/projects?api-version=7.1-preview.1&$top=5`
    );
    const projectRepoResults: string[][] = await Promise.all(
      topProjectsResults.value.map(async (project) => {
        const repos = await this.#get<AzureRepos>(
          token,
          `${this.host}/${org}/${project.name}/_apis/git/repositories?api-version=7.1-preview.1`
        );

        return repos.value.map(
          (it) =>
            `${encodeURIComponent(it.project.name)}/${encodeURIComponent(it.name)}`
        );
      })
    );

    return projectRepoResults.flatMap((it) => it);
  }

  async getLatestCommitsFromRepos(
    token: string,
    org: string,
    repos: string[]
  ): Promise<Commit[][]> {
    return await Promise.all(
      repos.map(async (repo) => {
        try {
          const [projectName, repoName] = repo.split('/');
          const latestCommits = await this.#get<AzureCommits>(
            token,
            `${this.host}/${org}/${projectName}/_apis/git/repositories/${repoName || projectName}/commits?searchCriteria.$top=100&api-version=7.0`
          );

          return latestCommits.value
            .filter((commit) => Boolean(commit.author.email))
            .map(({ author: { name, email }, url }) => ({
              email: email!,
              name,
              photoURL: determineDefaultAvatarForUser(name, email!),
              url
            }));
        } catch (error: any) {
          console.warn(`Failed to fetch commits for ${org}/${repo}:`, error);

          return [];
        }
      })
    );
  }

  async getAvatar(
    token: string,
    email: string,
    placeholder: string,
    commitUrl: string
  ): Promise<string> {
    // Can't search for user by email and the avatar is in the commit URL that is given when fetching commits. This commit has the author avatar
    const commitResult = await this.#get<AzureCommit>(token, commitUrl);

    return commitResult.author.imageUrl;
  }

  getNonCloudAuthenticatedUrl(url: string): { url: string; useAuth: boolean } {
    const imageUrl = new URL(url);
    const hostUrl = new URL(this.host);

    return {
      useAuth: imageUrl.hostname === hostUrl.hostname,
      url
    };
  }

  getAuthorizationHeader(token: string): string {
    return this.#usingPat ? `Basic ${btoa(`:${token}`)}` : `Bearer ${token}`;
  }

  readonly oauthPermissions = [
    {
      id: 'azure_requests',
      icon: 'logo-azure',
      iconUrl: 'img/azure_grey.svg',
      title: 'Requested OAuth scopes',
      subtitle:
        'When you authorize GitLive on your Azure DevOps account we request permission to the following resources'
    },
    {
      id: 'scopes',
      icon: '',
      scopes: [
        {
          id: 'code',
          icon: code,
          title: 'Code (read)',
          subtitle:
            'Grants the ability to read source code and metadata about commits, changesets and branches.',
          descriptionHTML:
            'Repository data is accessed via the API to show the latest changes from your remote repository without you having to perform git fetch locally.' +
            '<p style="padding-top: 4px">It is also used to display branches from private repositories in the organization view.</p>'
        },
        {
          id: 'graph',
          icon: folder,
          title: 'Graph (read)',
          subtitle:
            'Grants the ability to read user, group, scope and group membership information',
          descriptionHTML:
            'Your primary email address is used to identify and contact you and to link your account with commit data from your git repository.' +
            '<p style="padding-top: 4px">Additionally, your name and avatar is used to display to the other members of your organization.</p>'
        }
      ]
    }
  ];

  readonly tokenPermissions = [
    {
      id: 'azure_requests',
      icon: 'logo-azure',
      iconUrl: 'img/azure_grey.svg',
      title: 'Required scopes',
      subtitle:
        'The following scopes are required when creating the access token'
    },
    {
      id: 'scopes',
      icon: '',
      scopes: [
        {
          id: 'identity',
          icon: person,
          title: 'Identity (read)',
          subtitle: 'Provides the ability to read identities and groups.',
          descriptionHTML:
            'Your primary email address is used to identify and contact you and to link your account with commit data from your git repository.'
        },
        {
          id: 'code',
          icon: code,
          title: 'Code (read)',
          subtitle:
            'Grants the ability to read source code and metadata about commits, changesets and branches.',
          descriptionHTML:
            'Repository data is accessed via the API to show the latest changes from your remote repository without you having to perform git fetch locally.' +
            '<p style="padding-top: 4px">It is also used to display branches from private repositories in the organization view.</p>'
        },
        {
          id: 'graph',
          icon: folder,
          title: 'Graph (read)',
          subtitle:
            'Grants the ability to read user, group, scope and group membership information',
          descriptionHTML:
            'Your name and avatar is used to display to the other members of your organization.'
        }
      ]
    }
  ];

  async #get<T>(oauthToken: string, url: string): Promise<T> {
    const token = this.#usingPat ? btoa(`:${oauthToken}`) : oauthToken;

    return await get(url, token, '', this.#usingPat);
  }
}
